import { getReCAPTCHAToken } from "../eos/getReCAPTCHAToken";
import type { Dcs3Address } from "./index.types";
import type { UserApiBaseResponse } from "./types";
import type {
	CreateUserApiRequest,
	CreateUserApiResponse,
} from "./types/CreateUser";
import type { GetUserApiResponse } from "./types/GetUser";
import type {
	UpdateUserApiRequest,
	UpdateUserApiResponse,
	UpdateUserPasswordWithMFAApiRequest,
	UpdateUserWithMFAApiRequest,
	UpdateUserWithMFAApiResponse,
} from "./types/UpdateUser";
import useDCS3Api from "./useDCS3Api";

const useUserService = () => {
	const v2Api = useDCS3Api("V2");
	const v3Api = useDCS3Api("V3");

	return {
		get: {
			user: () => v2Api.get<GetUserApiResponse>("/customer"),
		},
		patch: {
			activateUser: (payload: { activationCode: string }) =>
				v2Api.patch("/admin/account/activation", payload),
		},
		post: {
			address: async (payload: Dcs3Address) => {
				const token = await getReCAPTCHAToken();
				const headers = { "X-DOMAIN-TOKEN": token };
				return v3Api.post<UserApiBaseResponse>(
					"/customer/address",
					payload,
					headers,
				);
			},
			forgotPassword: ({ email }: { email: string }) =>
				v2Api.post(`/admin/customer/password/${email}`),
			mfaCode: (payload: {
				otpPhone: string;
				otpEmail: string;
				useCase?: string;
			}) =>
				v2Api.post<UserApiBaseResponse>("/customer/mfa", {
					...payload,
					useCase: payload.useCase ?? "change-profile",
				}),
			resendActivationEmail: ({ email }: { email: string }) =>
				v2Api.post(`/admin/account/activation/email/${email}`),
			user: (payload: CreateUserApiRequest) =>
				v2Api.post<CreateUserApiResponse>("/admin/customer", payload),
		},
		put: {
			mfaUser: (payload: UpdateUserWithMFAApiRequest) =>
				v3Api.put<UpdateUserWithMFAApiResponse>("/customer", payload),
			mfaPassword: (payload: UpdateUserPasswordWithMFAApiRequest) =>
				v3Api.put<UpdateUserApiResponse>("/customer", payload),
			password: (payload: { newPassword: string; currentPassword: string }) =>
				v2Api.put("/customer/password", payload),
			phone: (payload: { otpCode: string; phoneNumber: string }) =>
				v2Api.put("/customer/phone", payload),
			resetPassword: (payload: { resetCode: string; newPassword: string }) =>
				v2Api.put("/admin/customer/password/notUsed?idType=tmsGuid", payload),
			user: (payload: UpdateUserApiRequest) =>
				v2Api.put<UpdateUserApiResponse>("/customer", payload),
		},
	};
};

export default useUserService;
